import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

const CONTACT_EMAIL = 'admin@syncanix.com';

/**
 * Vulnerability-disclosure policy (`/trust/disclosure`) — launch-readiness B5.
 *
 * The policy `/.well-known/security.txt` points at. Replaces the previous
 * security.txt fiction (a HackerOne program, a PGP key, a hall-of-fame and a
 * bug-bounty page that never existed) with commitments a solo-operated vendor
 * can actually keep: a real inbox, acknowledge ≤ 3 business days, triage ≤ 7
 * days, updates at least every 14 days, good-faith safe harbor, coordinated
 * 90-day disclosure, and an HONEST "no paid bounty programme today".
 *
 * Same content authority discipline as `/trust/security`: commitments here are
 * public projections of what we operate — change the operation, change this
 * page in the same PR. Pure presentational; `disclosure.*` i18n namespace; EN
 * legally authoritative (LegalTranslationNotice self-hides on EN).
 */
export function Disclosure(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('disclosure.meta.title'),
    description: t('disclosure.meta.description'),
    path: '/trust/disclosure',
  });
  return (
    <LegalPage
      breadcrumb={t('disclosure.breadcrumb')}
      title={t('disclosure.heading')}
      intro={t('disclosure.intro')}
      lastUpdatedSlug="disclosure"
    >
      <section aria-labelledby="disc-report" className="mb-10">
        <h2
          id="disc-report"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('disclosure.report.heading')}
        </h2>
        <p className="mb-3">
          {t('disclosure.report.body')}{' '}
          <a className={INLINE_LINK} href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('disclosure.report.include.repro')}</li>
          <li>{t('disclosure.report.include.impact')}</li>
          <li>{t('disclosure.report.include.surface')}</li>
        </ul>
      </section>

      <section aria-labelledby="disc-commit" className="mb-10">
        <h2
          id="disc-commit"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('disclosure.commitments.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('disclosure.commitments.ack')}</li>
          <li>{t('disclosure.commitments.triage')}</li>
          <li>{t('disclosure.commitments.updates')}</li>
          <li>{t('disclosure.commitments.credit')}</li>
        </ul>
        <p className="mt-3 text-[color:var(--bd-muted)]">{t('disclosure.commitments.noBounty')}</p>
      </section>

      <section aria-labelledby="disc-harbor" className="mb-10">
        <h2
          id="disc-harbor"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('disclosure.harbor.heading')}
        </h2>
        <p className="mb-3">{t('disclosure.harbor.body')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('disclosure.harbor.rule.privacy')}</li>
          <li>{t('disclosure.harbor.rule.noDegrade')}</li>
          <li>{t('disclosure.harbor.rule.ownTenant')}</li>
        </ul>
      </section>

      <section aria-labelledby="disc-scope" className="mb-10">
        <h2
          id="disc-scope"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('disclosure.scope.heading')}
        </h2>
        <p className="mb-3">{t('disclosure.scope.inScope')}</p>
        <p className="mb-2 font-medium">{t('disclosure.scope.outHeading')}</p>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('disclosure.scope.out.dos')}</li>
          <li>{t('disclosure.scope.out.social')}</li>
          <li>{t('disclosure.scope.out.thirdParty')}</li>
          <li>{t('disclosure.scope.out.scanner')}</li>
        </ul>
      </section>

      <section aria-labelledby="disc-coord" className="mb-10">
        <h2
          id="disc-coord"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('disclosure.coordinated.heading')}
        </h2>
        <p>{t('disclosure.coordinated.body')}</p>
      </section>

      <p className="text-sm text-[color:var(--bd-muted)]">
        {t('disclosure.securityTxt')}{' '}
        <a className={INLINE_LINK} href="/.well-known/security.txt">
          /.well-known/security.txt
        </a>
      </p>
    </LegalPage>
  );
}
